import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Entity } from '../../../classes/entity.class';
import { PlanningHistoryEntry } from '../../../classes/planning-history-entry';
import { User } from '../../../classes/user.class';
import { CodaltComponent } from '../../../codalt.component';
import { EntitiesService } from '../../../services/entities/entities.service';
import { EntityField, EntityType, FieldType } from '../../../services/entities/entity-type.class';
import { UserService } from '../../../services/user/user.service';

const FALLBACK_ID_LABELS: Record<string, { label: string; type: 'user' | 'entity' }> = {
    user_id: { label: 'Gebruiker', type: 'user' },
    driver_user_id: { label: 'Chauffeur', type: 'user' },
    entity_id: { label: 'Materieel', type: 'entity' },
};

@Component({
    selector: 'app-planning-history-dialog',
    standalone: false,
    templateUrl: './planning-history-dialog.component.html',
    styleUrl: './planning-history-dialog.component.scss',
})
export class PlanningHistoryDialogComponent extends CodaltComponent implements OnInit {
    users: User[] = [];
    entities: Entity[] = [];
    readonly dateFormat = 'EEE d MMM y HH:mm:ss';

    private readonly fieldMap: Record<string, EntityField>;
    private readonly driverIdAsOf: Map<PlanningHistoryEntry, number | null>;

    constructor(
        public dialogRef: MatDialogRef<PlanningHistoryDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { items: PlanningHistoryEntry[]; entityType: EntityType },
        private userService: UserService,
        private entitiesService: EntitiesService
    ) {
        super();
        this.fieldMap = {
            ...(this.data.entityType?.fields_planning ?? {}),
            ...(this.data.entityType?.fields_planning_data ?? {}),
            ...(this.data.entityType?.fields_phe ?? {}),
            ...(this.data.entityType?.fields_phe_data ?? {}),
        };
        this.driverIdAsOf = this.buildDriverTimeline(this.data.items ?? []);
    }

    // data.items is sorted newest-first; per materieel (source_row_id) we replay the
    // chauffeur-wijzigingen oud->nieuw zodat een regel de chauffeur toont van vóór zijn eigen
    // wijziging, en latere regels pas de nieuwe chauffeur zien.
    private buildDriverTimeline(items: PlanningHistoryEntry[]): Map<PlanningHistoryEntry, number | null> {
        const bySourceRow = new Map<number, PlanningHistoryEntry[]>();

        for (const item of items) {
            if (item.source !== 'planning_has_entity' || item.source_row_id == null) {
                continue;
            }
            if (!bySourceRow.has(item.source_row_id)) {
                bySourceRow.set(item.source_row_id, []);
            }
            bySourceRow.get(item.source_row_id).push(item);
        }

        const driverIdAsOf = new Map<PlanningHistoryEntry, number | null>();

        for (const group of bySourceRow.values()) {
            const chronological = [...group].reverse();
            let currentDriverId: number | null = null;

            const firstEntry = chronological[0];
            if (firstEntry?.field === 'driver_user_id' && firstEntry.old_value) {
                // De historie begint hier al met een wijziging: de oude waarde daarvan is de
                // enige chauffeur die we kennen van vóór onze zichtbare historie.
                currentDriverId = +firstEntry.old_value;
            }

            for (const entry of chronological) {
                if (entry.field === 'driver_user_id' && !entry.old_value) {
                    // Eerste toewijzing (geen vorige chauffeur): direct zichtbaar op deze regel zelf.
                    currentDriverId = entry.new_value ? +entry.new_value : null;
                    driverIdAsOf.set(entry, currentDriverId);
                    continue;
                }

                driverIdAsOf.set(entry, currentDriverId);

                if (entry.field === 'driver_user_id') {
                    currentDriverId = entry.new_value ? +entry.new_value : null;
                }
            }
        }

        return driverIdAsOf;
    }

    getDriverIdAsOf(item: PlanningHistoryEntry): number | null {
        return this.driverIdAsOf.get(item) ?? null;
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.userService.getList(true).subscribe(users => {
                this.users = users;
            })
        );

        this.subscriptions.add(
            this.entitiesService.getList().subscribe(entities => {
                this.entities = entities;
            })
        );
    }

    getLabel(field: string): string {
        return this.fieldMap[field]?.name ?? FALLBACK_ID_LABELS[field]?.label ?? field;
    }

    isDateField(item: PlanningHistoryEntry): boolean {
        return item.date_old_value != null || item.date_new_value != null;
    }

    isCheckboxField(field: string): boolean {
        return this.fieldMap[field]?.type === FieldType.Checkbox;
    }

    isTextareaField(field: string): boolean {
        return this.fieldMap[field]?.type === FieldType.Textarea;
    }

    isUserIdField(field: string): boolean {
        const type = this.fieldMap[field]?.type;
        return (
            type === FieldType.UserId ||
            type === FieldType.UserIdWithLeaves ||
            FALLBACK_ID_LABELS[field]?.type === 'user'
        );
    }

    isEntityIdField(field: string): boolean {
        return this.fieldMap[field]?.type === FieldType.EntityId || FALLBACK_ID_LABELS[field]?.type === 'entity';
    }
}
